import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import type { PdfItilFieldKey, PdfItilFieldSelection, PdfItilOverrides } from '../types';
import { itilColors } from '../styles';

type FieldSelection = PdfItilFieldSelection & {
  selected: 'original' | 'itil';
};

type Props = {
  open: boolean;
  fields: PdfItilFieldSelection[];
  onClose: () => void;
  onExport: (overrides: PdfItilOverrides) => void;
  isExporting?: boolean;
};

function ItilPdfReviewModal({
  open,
  fields,
  onClose,
  onExport,
  isExporting = false
}: Props) {
  const { t } = useTranslation('global');

  const [selections, setSelections] = useState<FieldSelection[]>([]);

  useEffect(() => {
    if (open) {
      setSelections(
        fields.map((f) => ({
          ...f,
          selected: f.itilText ? 'itil' : 'original'
        }))
      );
    }
  }, [open, fields]);

  const summary = useMemo(() => {
    const itil = selections.filter((s) => s.selected === 'itil').length;
    const orig = selections.filter((s) => s.selected === 'original').length;
    return { itil, orig };
  }, [selections]);

  const handleSelect = useCallback(
    (key: PdfItilFieldKey, version: 'original' | 'itil') => {
      setSelections((prev) =>
        prev.map((f) => {
          if (f.key !== key) {
            return f;
          }
          if (version === 'itil' && !f.itilText) {
            return f;
          }
          return { ...f, selected: version };
        })
      );
    },
    []
  );

  const handleExport = () => {
    const overrides: PdfItilOverrides = {};
    selections.forEach((s) => {
      overrides[s.key] =
        s.selected === 'itil' && s.itilText ? s.itilText : s.originalText;
    });
    onExport(overrides);
  };

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pb: 1 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '9px',
            bgcolor: itilColors.navy,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 13,
            flexShrink: 0
          }}
        >
          ITIL
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, color: itilColors.navy, fontSize: 20 }}>
            {t('itilGenius.pdfReview.title')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: itilColors.inkSoft, mt: 0.5 }}>
            {t('itilGenius.pdfReview.subtitle')}
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ fontSize: 12, color: itilColors.inkSoft, mb: 2 }}>
          {t('itilGenius.pdfReview.fieldCount', { count: selections.length })}
        </Typography>

        {selections.map((field, idx) => {
          const itilUnavailable = !field.itilText;
          return (
            <Box
              key={field.key}
              sx={{
                border: '1px solid #e3e8ef',
                borderRadius: '9px',
                mb: 2.5,
                overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(31,56,100,.06)'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '13px 18px',
                  bgcolor: '#f7f9fc',
                  borderBottom: '1px solid #e7ecf2'
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '5px',
                    bgcolor: itilColors.navy,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700
                  }}
                >
                  {idx + 1}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: itilColors.navy,
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  {field.label}
                </Typography>
                {itilUnavailable && (
                  <Typography
                    sx={{
                      ml: 'auto',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      color: '#74829a',
                      textTransform: 'uppercase'
                    }}
                  >
                    {t('itilGenius.pdfReview.notAvailable')}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
                }}
              >
                {(['original', 'itil'] as const).map((version) => {
                  const isItil = version === 'itil';
                  const disabled = isItil && itilUnavailable;
                  const selected = field.selected === version;
                  const text = isItil ? field.itilText : field.originalText;

                  return (
                    <Box
                      key={version}
                      onClick={() => !disabled && handleSelect(field.key, version)}
                      sx={{
                        bgcolor: isItil ? itilColors.itilBg : itilColors.origBg,
                        borderLeft: isItil ? '1px solid #e3e8ef' : 'none',
                        boxShadow: selected ? `inset 0 0 0 2px ${itilColors.navy}` : 'none',
                        opacity: disabled ? 0.55 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: '13px 18px',
                          borderBottom: `1px solid ${isItil ? itilColors.itilBorder : itilColors.origBorder}`
                        }}
                      >
                        <Radio checked={selected} disabled={disabled} size="small" />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                          {isItil
                            ? t('itilGenius.pdfReview.exportItil')
                            : t('itilGenius.pdfReview.exportOriginal')}
                        </Typography>
                        {isItil && !itilUnavailable && (
                          <Typography sx={{ ml: 'auto', fontSize: 11, color: '#8a94a4' }}>
                            {t('itilGenius.pdfReview.recommended')}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ p: '16px 18px', maxHeight: 200, overflow: 'auto' }}>
                        {disabled ? (
                          <Box
                            sx={{
                              bgcolor: '#f4f6f9',
                              border: '1px dashed #c9d4e2',
                              borderRadius: '7px',
                              p: 1.5,
                              fontSize: '12.5px',
                              color: itilColors.inkSoft
                            }}
                          >
                            {t('itilGenius.pdfReview.naBody')}
                          </Box>
                        ) : (
                          <Typography
                            sx={{ fontSize: '13.5px', lineHeight: 1.62, whiteSpace: 'pre-wrap' }}
                          >
                            {text || t('ticket.pdf.noData')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 12, color: itilColors.inkSoft }}>
          {t('itilGenius.pdfReview.summary', {
            itil: summary.itil,
            original: summary.orig,
            total: selections.length
          })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button onClick={onClose} variant="outlined">
            {t('common.commonActions.cancel')}
          </Button>
          <Button
            disabled={isExporting}
            onClick={handleExport}
            sx={{ bgcolor: itilColors.navy }}
            variant="contained"
          >
            {isExporting
              ? t('itilGenius.pdfReview.generating')
              : t('itilGenius.pdfReview.export')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ItilPdfReviewModal;
